import { storeToRefs } from 'pinia';
import { toRefs } from 'vue';
import { useLocationStore } from '@globals/stores/location';
import type { GeoTreeLocation, GeoTreeLocationItem } from '@globals/types/GeoTree';
import { useServicesStore } from '@customer/stores/services';
import type {
	ServicesFilterGroup, ServicesFilter, ServicesFilterGroupBackend, ServicesFilterEntry, ServicesFilterBackend, ServicesAutomaticRecommendationsSettings, ServicesSizeFilter, ServicesMatchingAnswer, ServicesMatchingQuestion, ServicesContactPerson, ServicesContactPersonBackend, ServicesObject, ServicesUserActivityStats, ServicesStats,
} from '@customer/types/Services';

export default (servicesConstuructor?:unknown,processResultConstructor?:unknown) => {
	const services = servicesConstuructor?.value || useServicesStore()?.services;
	const processResult = processResultConstructor?.value || useServicesStore()?.processResult;

	/**
 	* Zlicza statystyki dla wszystkich filtrów: czy istnieją usługi dodatkowe,
 	* czy limity punktów dla Polecania nie przekraczają stanu konta, etc.
 	* @param filter Kolejny zliczany filtr z listy
 	*/
	const statsCount = (filter: ServicesFilter) => {
		const { services } = useServicesStore();
		const { stats } = services;
		const { isActive, type, automaticRecommendations } = filter;

		if (!isActive) { stats.inactiveCount++; }

		if (type === 'quick') { stats.withQuick = true; }

		if (automaticRecommendations && type === 'automatic') {
			stats.withAutomatic = true;

			if (isActive) {
				stats.setLimitSum += automaticRecommendations.weeklyLimit;
			}

			if (automaticRecommendations.needsModeration) {
				stats.needsModeration = true;
			}

			if (automaticRecommendations.notEnoughBalance) {
				stats.notEnoughBalance = true;
			}
		}
	};

	/**
 * Resetuje statystyki dla wszystkich filtrów.
 */
	const clearStatsCount = () => {
		const { services } = useServicesStore();
		services.stats = {
			hasInactiveFilterGroups: false,
			notEnoughBalance: false,
			setLimitSum: 0,
			needsModeration: false,
			withAutomatic: false,
			withQuick: false,
			inactiveCount: 0,
			activeGroupCount: 0,
			hasGroupedCategories: false,
			groupLimitCheck: '',
		};
	};

	/**
 * Przekształca listę osób kontaktowych z postaci backendową na frontendową.
 * @param contactPersons Tablica osób kontaktowych
 */
	const getContactPersons = (contactPersons: ServicesContactPersonBackend[]): ServicesContactPerson[] => {
		return contactPersons.map((person) => ({
			id: person.id,
			fullName: person.fullName,
			phone: person.phone,
			email: person.login,
		}));
	};

	/**
 * Przekształca listę Operatorów Oferteo z postaci backendową na frontendową.
 * @param operators Tablica Operatorów Oferteo
 */
	const getOperators = (operators: ServicesContactPersonBackend[]): ServicesContactPerson[] => {
		return operators.map((operator) => ({
			id: operator.idCustomer,
			fullName: operator.fullName,
			phone: operator.phone,
			email: operator.login,
		}));
	};

	const getRootFromName = (name: string, tree: Record<string, GeoTreeLocation>) => {
		for (const root of Object.keys(tree)) {
			if (name.startsWith(root)) {
				return tree[root];
			}
		}
		return undefined;
	};

	/**
 * Pobiera drzewo lokalizacji wraz z ich graficzną reprezentacją
 * z pliku `scripts/geo.js` lub, jeśli wcześniej jego zawartość
 * została pobrana - z `localStorage`.
 */
	const growTree = async () => {
		const JSON_VERSION = 7;
		const JSON_PATH = `/scripts/geo.js?rev=${JSON_VERSION}`;
		const JSON_TREE_KEY = 'geotree';
		const treeFromLocalStorage = window.localStorage[JSON_TREE_KEY];
		if (treeFromLocalStorage) {
			const { data, version } = JSON.parse(treeFromLocalStorage) as {
				data: Record<string, GeoTreeLocation>;
				version: number;
			};
			const normalizedData = {} as Record<string, GeoTreeLocation>;
			Object.entries(data).forEach((group) => {
				const [key, values] = group;
				const normalizedValues = Object.entries(values).reduce((prev, current) => {
					const [valueKey, value] = current;
					const valueToLowerCase = valueKey.toLowerCase();
					if (valueToLowerCase === 'items') {
						Object.entries(value).forEach((entry) => {
							const [keyItems, valueItems] = entry as [string, GeoTreeLocationItem];
							const normalizedItemsValues = Object.entries(valueItems).reduce((prevItems, currentItems) => {
								const [itemsValueKey, itemsValue] = currentItems;
								const itemsValueToLowerCase = itemsValueKey.toLowerCase();
								return { ...prevItems, [itemsValueToLowerCase]: itemsValue };
							}, {});
							value[keyItems] = normalizedItemsValues;
						});
					}
					return { ...prev, [valueToLowerCase]: value };
				}, {});
				normalizedData[key] = normalizedValues as GeoTreeLocation;
			});
			if (version >= JSON_VERSION) {
				return normalizedData;
			}
		}
		const { countries, poland } = useLocationStore();
		const locs = {
			Countries: countries,
			Poland: poland,
		};
		const tree = {} as Record<string, GeoTreeLocation>;
		const domestic = locs.Poland;
		domestic.forEach(({ Value, Label }: { Value: string; Label: string }) => {
			tree[Value] = {
				name: Label,
				selected: 0,
				items: {},
				expanded: false,
			};
		});
		const foreign = locs.Countries;
		tree.locationForeign = {
			name: 'Zagranica',
			selected: 0,
			items: {},
			expanded: false,
		};
		foreign.forEach(({ Value, Label }: { Value: string; Label: string }) => {
			if (Value !== 'locationPl') {
				tree.locationForeign.items[Value] = {
					name: Label,
					selected: false,
				};
			}
		});
		const rsvp = await fetch(JSON_PATH).then((data) => data.json()) as Record<string, {
			name: string;
			geojson: { type: string; coordinates: unknown[] };
			cities: { v: string; n: string }[];
		}>;
		const set = (
			items: Record<string, GeoTreeLocationItem>,
			index: string,
			locationObject: GeoTreeLocationItem,
		) => {
			items[index] = locationObject;
		};
		for (const index in rsvp) {
			const location = rsvp[index];
			const root = getRootFromName(index, tree);
			if (!root) { continue; }
			set(root.items, index, {
				name: location.name,
				selected: false,
				geo: location.geojson,
			});
			if (!location.cities) {
				continue;
			}
			location.cities.forEach((city, cityIndex: number) => {
				const cityRoot = getRootFromName(city.v, tree);
				if (!cityRoot) { return; }
				set(cityRoot.items, city.v, {
					name: city.n,
					selected: false,
					geo: {
						type: 'Polygon',
						coordinates: [
							location.geojson.coordinates[Number(cityIndex) + 1],
						],
					},
				});
			});
		}
		window.localStorage[JSON_TREE_KEY] = JSON.stringify({
			version: JSON_VERSION,
			data: tree,
		});
		return tree;
	};

	/**
 * Przekształca typ filtra na postać widoczną dla użytkownika
 */
	const processItemLabel = (type: string) => {
		let typeLabel = '';
		switch (type) {
			case 'free':
				typeLabel = 'Dopasowanie';
				break;
			case 'quick':
				typeLabel = 'Szybki kontakt';
				break;
			case 'automatic':
				typeLabel = 'Polecanie';
				break;
			default:
				break;
		}
		return { typeLabel };
	};

	/**
 * Przekształca nazwę kategorii z postaci backendowej na uproszczoną.
 */
	const processCategoryName = (backendFilter: ServicesFilterBackend) => {
		const { categoryListForView, category, isLeaf } = backendFilter;
		let formattedCategoryName = categoryListForView;
		formattedCategoryName = formattedCategoryName.split('<br/>').join(', ');
		if (formattedCategoryName.indexOf(' / ') !== -1) {
			const split = formattedCategoryName.split(' / ');
			formattedCategoryName = split[1] === 'Pozostałe' ? formattedCategoryName.replace(' / ', ' › ') : split[1];
		}
		if (formattedCategoryName === '' && category) {
			formattedCategoryName = category.name;
		}
		if (!isLeaf) {
			formattedCategoryName = `[BRANCH] ${formattedCategoryName}`;
		}
		return formattedCategoryName;
	};

	/**
 * Przekształca 9-ciocyfrowy numer telefonu na grupę po trzy cyfry.
 */
	const formatPhoneNumber = (phoneNumber: string) => {
		const formattedPhoneNumber = `${phoneNumber}`;
		return formattedPhoneNumber.length !== 9
			? formattedPhoneNumber
			: formattedPhoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
	};

	/**
 * Przekształca tablicę zaznaczonych lokalizację na łańcuch znaków.
 */
	const processSelectedLocationsLabel = (
		locations: string[],
		allLocationsRaw?: Record<string, GeoTreeLocation>,
	): string => {
		const allLocations = allLocationsRaw || JSON.parse(JSON.stringify(services.locations)) as Record<string, GeoTreeLocation>;
		const result: string[] = [];
		const poland: Record<string, number> = {};
		const foreign: string[] = [];
		for (const index of locations) {
			if (index.startsWith('locationPl')) {
				for (const allLocationIndex in allLocations) {
					if (index === allLocationIndex) {
						poland[allLocations[allLocationIndex].name] = -1;
						continue;
					}
					if (allLocations[allLocationIndex].items[index]) {
						poland[allLocations[allLocationIndex].name] = poland[allLocations[allLocationIndex].name]
							? poland[allLocations[allLocationIndex].name] + 1
							: 1;
						continue;
					}
				}
				continue;
			}
			const name = allLocations.locationForeign.items[index]?.name;
			if (typeof name === 'string') {
				foreign.push(name);
			}
		}
		for (const index in poland) {
			const item = poland[index];
			result.unshift(
				item === -1
					? index
					: `${index} (${item} powiat${item === 1 ? '' : item < 5 ? 'y' : 'ów'})`,
			);
		}
		result.push(...foreign);
		return result.join(', ');
	};
	/**
 * Pobiera ustawioną osobę kontaktową z podanego filtru lub pierwszą lepszą z obiektu usług.
 */
	const processContactPerson = (backendFilter: ServicesFilterBackend) => {
		const contactPersons = services.contactPersons;
		const contactPerson = contactPersons.find((person: ServicesContactPerson) => person.id === backendFilter.idCustomerContactUser) || contactPersons[0];
		return {
			id: backendFilter.idCustomerContactUser,
			contactPersonId: backendFilter.idCustomerContactUser,
			fullName: contactPerson?.fullName || '',
			email: contactPerson?.email || '',
			phone: contactPerson?.phone || '',
		};
	};

	/**
 * Przekształca informacje o wielkości filtra na łańcuch znaków.
 */
	const processSizeLabel = (backendFilter: ServicesFilterBackend) => {
		return backendFilter.sizeUnit
			? `Wielkość ${backendFilter.sizeFrom ? `od ${backendFilter.sizeFrom}` : ''} ${backendFilter.sizeTo ? `do ${backendFilter.sizeTo}` : ''} ${backendFilter.sizeUnit}`
			: '';
	};

	/**
 * Przekształca informacje o ustawieniach Polecania filtra z postaci backendowej do frontendowej.
 */
	const processAsAutomatic = (backendFilter: ServicesFilterBackend): ServicesAutomaticRecommendationsSettings => {
		const price = backendFilter.contactPrice || backendFilter.contactPrice === 0 ? backendFilter.contactPrice : backendFilter.categoryDefaultPrice;
		return {
			canBeEnabled: backendFilter.canEnableAP,
			isCategoryValid: backendFilter.isCategoryInAP,
			isCategoryVerified: backendFilter.filterCategories[0].verified,
			needsModeration: backendFilter.imNeedsModeration,
			enabledBy: [backendFilter.apEnabledOperator, backendFilter.tsApEnabled],
			disabledBy: [backendFilter.apDisabledOperator, backendFilter.tsApDisabled],
			weeklyLimit: backendFilter.imWeeklyLimit && backendFilter.idType === 'mgFltrTypeIm' ? backendFilter.imWeeklyLimit : price * 10,
			weeklyLimitMin: Math.round(price * 2),
			weeklyLimitMax: price * 50,
			smsNumber: {
				enabled: backendFilter.imSmsNotification,
				number: backendFilter.customer ? formatPhoneNumber(backendFilter.customer.contactPersonPhone) : '',
				isValid: backendFilter.phoneCheck === 'Komórkowy poprawny',
			},
			recommendedClientsCount: backendFilter.recommendClientsCount,
			contactedClientsCount: backendFilter.contactClientsCount,
			remainingPoints: backendFilter.remainPoints,
			notEnoughBalance: backendFilter.notEnoughBalance,
		};
	};
	/**
 * Przekształca typ filtra z postaci używanej w backendzie na tą używaną
 * we frontendzie i vice versa
 */
	const getItemType = ({
		type,
		reverse = false,
	}: {
		type: string;
		reverse?: boolean;
	}) => {
		const back = ['mgFltrTypeNotif', 'mgFltrTypeSk', 'mgFltrTypeIm'];
		const front = ['free', 'quick', 'automatic'];
		const result = (reverse ? back : front)[(reverse ? front : back).indexOf(type)];

		services.itemType = result;

		return result;
	};
	/**
 * Przekształca informacje o filtrze z postaci backendowej do frontendowej.
 * @param filterDetails Filtr do przekształcenia
 * @param cloneType Czy mamy sklonować dany filtr? (Ustawić `id`)
 */
	const process = ({ filterDetails, cloneType = '' }: { filterDetails: ServicesFilterBackend; cloneType?: string }): ServicesFilter | undefined => {
		const backendFilter = filterDetails.filter
			? filterDetails.filter[0] || filterDetails.filter
			: filterDetails;
		if (!backendFilter.filterCategories.length) {
			return undefined;
		}
		if (cloneType) {
			backendFilter.id = 0;
			backendFilter.idType = getItemType({
				type: cloneType,
				reverse: true,
			});
		}
		const type = getItemType({ type: backendFilter.idType });
		const locationsIds = backendFilter.elementId
			? backendFilter.filterLocations.map(({ idLocation }) => idLocation)
			: [];
		const isActive = backendFilter.idStatus === 'mgFltrStatActive' || backendFilter.idStatus === 'mgFltrStatCalcOnly';
		const emptySizeFilter: ServicesSizeFilter = {
			unit: '',
			minValue: 0,
			minLimit: 0,
			maxValue: 0,
			maxLimit: 1000000,
		};
		const result: ServicesFilter = {
			id: backendFilter.id,
			category: {
				categoryId: backendFilter.filterCategories[0].idCategory,
				name: processCategoryName(backendFilter),
				parentId: '',
				isGeneraled: '',
			},
			isRemote: backendFilter.isRemote || false,
			remoteLocation: backendFilter.remoteLocation || 'both',
			remoteType: backendFilter.remoteType || 'hybrid',
			type,
			...processItemLabel(type),
			sizeLabel: processSizeLabel(backendFilter),
			automaticRecommendations: processAsAutomatic(backendFilter),
			contactPrice: backendFilter.contactPrice,
			contactPerson: processContactPerson(backendFilter),
			isActive,
			idType: backendFilter.idType,
			isInitiallyActive: isActive,
			locations: {} as Record<string, GeoTreeLocation>,
			location: {
				mode: backendFilter.locationMode || 'select',
				selected: locationsIds,
				radius: backendFilter.radius > 0 ? backendFilter.radius : 50,
				center: null, // brak customer.locationCenter
				labels: processSelectedLocationsLabel(locationsIds),
			},
			quickContactSmsNumber: backendFilter.smsNumber,
			minContactPrice: backendFilter.minContactPrice,
			maxContactPrice: backendFilter.maxContactPrice,
			upcommingPriceChange: backendFilter.upcommingPriceChange,
			isApSuspended: backendFilter.isApSuspended,
			tsSuspendedEnd: backendFilter.tsSuspendedEnd,
			filterTypes: {
				size: backendFilter.sizeUnit
					? {
						unit: backendFilter.sizeUnit.toUpperCase(),
						minValue: backendFilter.sizeFrom,
						minLimit: 0,
						maxValue: backendFilter.sizeTo,
						maxLimit: 1000000,
					} as ServicesSizeFilter
					: emptySizeFilter,
				age: (backendFilter.usedFrom || backendFilter.usedTo) && (backendFilter.usedFrom > -1 || backendFilter.usedTo > -1)
					? {
						unit: 'YEARS',
						minValue: backendFilter.usedFrom,
						minLimit: 0,
						maxValue: backendFilter.usedTo,
						maxLimit: 100,
					} as ServicesSizeFilter
					: emptySizeFilter,
				discounted: backendFilter.notificationWithOutlet,
				standardRfps: backendFilter.standardRfps,
				discountedRfps: backendFilter.outletRfps,
				companies: backendFilter.companies,
				individuals: backendFilter.individuals,
				frequency: backendFilter.idNotificationMode,
				contractors: true,
				investors: true,
				subcontractors: true,
				notConfirmedBySms: backendFilter.notConfirmedSmsRfps,
				hideNotConfirmedBySms: backendFilter.hideNotConfirmedBySms,
			},
			mpo: backendFilter.mpo,
			idCustomerContactUser: backendFilter.idCustomerContactUser,
			notEnoughPoints: backendFilter.notEnoughPoints,
			finalMarkBelowApThreshold: backendFilter.finalMarkBelowApThreshold,
			weeklyLimitReached: backendFilter.weeklyLimitReached,
		};

		processResult.value = { ...result };
		return result;
	};
	/**
 * Oblicza liczbę kategorii dla limitu posiadanych przez użytkownika filtrów.
 * Wliczane są tylko aktywne filtry powiadomień. Na dodatek, jeżeli kategoria
 * jest `isGeneraled` (np. wózki widłowe), policz tylko jej rodzica.
 */
	const groupLimitRecalculate = () => {
		let hasGroups = false;
		let count = 0;

		const groups: any = {};

		for (const group of services.tree) {
			const filter = group.filters.find(({ type }) => type === 'free');

			if (!group.isActive || !filter) {
				continue;
			}

			const category = { ...filter.category };
			let ignore = false;

			if (!category.isGeneraled) {
				if (groups[category.parentId]) {
					ignore = true;
					hasGroups = true;
				} else {
					groups[category.parentId] = true;
				}
			}

			if (!ignore) {
				count++;
			}
		}

		services.stats.hasGroupedCategories = hasGroups;
		services.stats.activeGroupCount = count;
	};


	return {
		statsCount,
		clearStatsCount,
		getContactPersons,
		getOperators,
		getRootFromName,
		growTree,
		processItemLabel,
		processCategoryName,
		formatPhoneNumber,
		processSelectedLocationsLabel,
		processContactPerson,
		processSizeLabel,
		processAsAutomatic,
		process,
		getItemType,
		groupLimitRecalculate,
	};
};
